import { Stack, Typography } from "@mui/material";
import {
  EMPTY_CHARACTER,
  getIconProps,
  ICON_SIZE,
} from "@pulse/shared-components";
import {
  Spacing,
  Typography as LeoTypography,
  FoundationColorTokens,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React from "react";
import { UploadValidationLoader } from "../../../../components/fileUpload/UploadValidationLoader";
import { Instance } from "mobx-state-tree";
import {
  NavigateToFunctions,
  processProjectParams,
} from "../../../../routes/RoutesHelper";
import { TFunction } from "i18next";
import {
  SPoCDownloadAndUnmapStore,
  ValidateCSVAndUnmapSPoCsErrors,
} from "../../store/SPoCDownloadAndUnmapStore";
import { AlertCircle } from "lucide-react";
import { FileUploadComponent } from "../../../../components/fileUpload/FileUploadComponent";
import { FileUploadedDescription } from "../../../../components/fileUpload/FileUploadedDescription";
import { getUploadErrorDescriptions } from "../../../utils/FileUploadUtils";
import {
  MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES,
  MIN_FILE_SIZE_IN_BYTES,
} from "@pulse/shared-components";

export interface UnmapMultipleSPoCsUploadPaneProps {
  spocDownloadAndUnmapStore: Instance<typeof SPoCDownloadAndUnmapStore> | null;
  navigateTo: NavigateToFunctions;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
}

export const UnmapMultipleSPoCsUploadPane = observer(
  ({
    spocDownloadAndUnmapStore,
    navigateTo,
    t,
    spacing,
    typography,
    tokens,
  }: UnmapMultipleSPoCsUploadPaneProps): React.ReactElement => {
    const iconProps = getIconProps(tokens.iconError, ICON_SIZE.default);

    const getUploadErrors = (): React.ReactElement => {
      let errorDescription: string = EMPTY_CHARACTER;
      if (spocDownloadAndUnmapStore?.validationError) {
        errorDescription = getUploadErrorDescriptions(
          spocDownloadAndUnmapStore?.validationError,
          t,
          MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES,
          MIN_FILE_SIZE_IN_BYTES,
        );
      } else if (spocDownloadAndUnmapStore?.rpcErrors) {
        switch (spocDownloadAndUnmapStore?.rpcErrors) {
          case ValidateCSVAndUnmapSPoCsErrors.SpocAssignmentInProgress: {
            errorDescription = t(
              "projects.respondentList.respondentUpdateProgressDescription",
            );
            break;
          }
          case ValidateCSVAndUnmapSPoCsErrors.ProjectArchived: {
            errorDescription = t(
              "projects.respondentList.projectArchivedDescription",
            );
            break;
          }
          case ValidateCSVAndUnmapSPoCsErrors.UnknownFile: {
            errorDescription = t(
              "common.uploadDialog.unexpectedErrorDescription",
            );
            break;
          }
          case ValidateCSVAndUnmapSPoCsErrors.InvalidFile: {
            errorDescription = t(
              "projects.respondentList.spocAssignmentInvalidFileErrorDescription",
            );
            break;
          }
          default: {
            errorDescription = t(
              "common.uploadDialog.unexpectedErrorDescription",
            );
          }
        }
      }

      return (
        <Stack gap={spacing.spaceXXS}>
          <Typography {...typography.s2} color={tokens.label}>
            {t("projects.respondentList.fileDetails")}
          </Typography>
          <Typography {...typography.b2} color={tokens.label}>
            {spocDownloadAndUnmapStore?.fileName}
          </Typography>
          <Stack direction="row" gap={spacing.spaceXXS}>
            <AlertCircle {...iconProps} />
            <Typography {...typography.b2} color={tokens.labelError}>
              {errorDescription}
            </Typography>
          </Stack>
        </Stack>
      );
    };

    const fileUploadHandler = (file: File): Promise<void> => {
      spocDownloadAndUnmapStore?.setFileName(file.name);
      return processProjectParams(async (projectId) => {
        await spocDownloadAndUnmapStore?.uploadCSVFile(file, projectId);
      }, navigateTo);
    };

    if (spocDownloadAndUnmapStore?.isRPCLoading) {
      return (
        <UploadValidationLoader
          fileName={spocDownloadAndUnmapStore?.fileName}
          t={t}
          typography={typography}
          tokens={tokens}
          spacing={spacing}
        />
      );
    } else if (
      spocDownloadAndUnmapStore?.validationError ??
      spocDownloadAndUnmapStore?.rpcErrors
    ) {
      if (spocDownloadAndUnmapStore?.isFileInvalid) {
        return (
          <Stack gap={spacing.spaceMD}>
            <FileUploadComponent fileUploadHandler={fileUploadHandler} />
            <Stack gap={spacing.spaceXXS}>
              <Typography {...typography.s2} color={tokens.label}>
                {t("common.uploadDialog.errorsFoundText")}
              </Typography>
              <Typography
                {...typography.b2}
                color={tokens.labelError}
                whiteSpace="pre-line"
              >
                {spocDownloadAndUnmapStore.invalidFileDetails}
              </Typography>
            </Stack>
          </Stack>
        );
      }
      return (
        <Stack gap={spacing.spaceMD}>
          <FileUploadComponent fileUploadHandler={fileUploadHandler} />
          {getUploadErrors()}
        </Stack>
      );
    } else {
      if (spocDownloadAndUnmapStore?.hasFileUploaded) {
        return (
          <FileUploadedDescription
            fileName={spocDownloadAndUnmapStore?.fileName}
            t={t}
            typography={typography}
            tokens={tokens}
            spacing={spacing}
          />
        );
      } else {
        return <FileUploadComponent fileUploadHandler={fileUploadHandler} />;
      }
    }
  },
);
